import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Building2, FileText, Users, LogOut, Briefcase, DollarSign, Settings, ClipboardList, CheckSquare, Construction, Clock, Brain, MessageSquareWarning } from 'lucide-react';
import axios from 'axios';
import InstallAppButton from '@/components/InstallAppButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [mieAttivita, setMieAttivita] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  useEffect(() => {
    fetchMieAttivita();
  }, []);

  const fetchMieAttivita = async () => {
    try {
      const response = await axios.get(`${API}/mie-attivita`, getAuthHeaders());
      setMieAttivita(response.data);
    } catch (error) {
      console.error('Errore nel caricamento attività:', error);
      // Non mostrare errore, semplicemente non mostrare la card se fallisce
    }
  };

  // Menu items per ruolo
  const menuItems = [
    {
      id: 'clienti',
      title: 'Gestione Clienti',
      description: 'Visualizza e gestisci i clienti',
      icon: Building2,
      color: 'bg-blue-600',
      hoverColor: 'hover:bg-blue-700',
      path: '/clienti',
      roles: ['admin', 'master']
    },
    {
      id: 'preventivi',
      title: 'Preventivi',
      description: 'Gestisci preventivi e offerte',
      icon: FileText,
      color: 'bg-amber-600',
      hoverColor: 'hover:bg-amber-700',
      path: '/preventivi',
      roles: ['admin', 'master']
    },
    {
      id: 'ordini',
      title: 'Ordini di Lavoro',
      description: 'Gestisci ordini di lavoro',
      icon: Briefcase,
      color: 'bg-green-600',
      hoverColor: 'hover:bg-green-700',
      path: '/ordini',
      roles: ['admin', 'master']
    },
    {
      id: 'fatturazione',
      title: 'Fatturazione',
      description: 'Gestisci fatture e SAL da fatturare',
      icon: DollarSign,
      color: 'bg-purple-600',
      hoverColor: 'hover:bg-purple-700',
      path: '/fatturazione',
      roles: ['admin', 'master']
    },
    {
      id: 'accordi-quadro',
      title: 'Accordi Quadro',
      description: 'Gestisci contratti quadro e rendicontazione',
      icon: FileText,
      color: 'bg-violet-600',
      hoverColor: 'hover:bg-violet-700',
      path: '/accordi-quadro',
      roles: ['admin', 'master']
    },
    {
      id: 'cantieri',
      title: 'Cantieri',
      description: 'Gestisci cantieri e commesse',
      icon: Construction,
      color: 'bg-yellow-600',
      hoverColor: 'hover:bg-yellow-700',
      path: '/cantieri',
      roles: ['admin', 'master', 'guest'],
      checkAmbito: 'ingegnere'
    },
    {
      id: 'progetti',
      title: 'Progetti (Ingegneria)',
      description: 'Gestisci progetti tecnici',
      icon: Settings,
      color: 'bg-indigo-600',
      hoverColor: 'hover:bg-indigo-700',
      path: '/progetti',
      roles: ['admin', 'master'],
      checkAmbito: false
    },
    {
      id: 'miei-progetti',
      title: 'I Miei Progetti',
      description: 'Dashboard personale progetti',
      icon: ClipboardList,
      color: 'bg-teal-600',
      hoverColor: 'hover:bg-teal-700',
      path: '/miei-progetti',
      roles: ['admin', 'master', 'guest']
    },
    {
      id: 'controllo-gestione',
      title: 'Controllo Gestione',
      description: 'Traccia ore e attività per progetto',
      icon: Clock,
      color: 'bg-cyan-600',
      hoverColor: 'hover:bg-cyan-700',
      path: '/controllo-gestione',
      roles: ['admin', 'master', 'guest']
    },
    // AI Assistant disabilitato
    /*{
      id: 'ai-assistant',
      title: 'AI Assistant',
      description: 'Analisi AI di progetti, cantieri e fatturazione',
      icon: Brain,
      color: 'bg-gradient-to-r from-purple-600 to-blue-600',
      hoverColor: 'hover:from-purple-700 hover:to-blue-700',
      path: '/ai-assistant',
      roles: ['admin', 'master']
    },*/
    {
      id: 'reports',
      title: 'Report e Statistiche',
      description: 'Visualizza report e statistiche',
      icon: FileText,
      color: 'bg-orange-600',
      hoverColor: 'hover:bg-orange-700',
      path: '/reports',
      roles: ['admin', 'master']
    },
    {
      id: 'feedback',
      title: 'Feedback Utenti',
      description: 'Bug e suggerimenti degli utenti',
      icon: MessageSquareWarning,
      color: 'bg-orange-600',
      hoverColor: 'hover:bg-orange-700',
      path: '/feedback-management',
      roles: ['admin']
    },
    {
      id: 'utenti',
      title: 'Gestione Utenti',
      description: 'Amministra utenti del sistema',
      icon: Users,
      color: 'bg-red-600',
      hoverColor: 'hover:bg-red-700',
      path: '/utenti',
      roles: ['admin']
    },
    {
      id: 'impostazioni',
      title: 'Impostazioni',
      description: 'Configura integrazioni e parametri',
      icon: Settings,
      color: 'bg-purple-600',
      hoverColor: 'hover:bg-purple-700',
      path: '/impostazioni',
      roles: ['admin']
    }
  ];

  // Filtra menu in base al ruolo utente e ambito
  // Helper per verificare se l'utente ha un determinato ambito (supporta sia ambito singolo che ambiti multipli)
  const userHasAmbito = (ambito) => {
    if (user.ambiti && user.ambiti.length > 0) {
      return user.ambiti.includes(ambito);
    }
    return user.ambito === ambito;
  };
  
  const visibleMenuItems = menuItems.filter(item => {
    // Admin e Master vedono tutto
    if (user.ruolo === 'admin' || user.ruolo === 'master') {
      // Master Fattibilità vedono solo alcuni menu
      if (user.ruolo === 'master' && userHasAmbito('fattibilita')) {
        return ['accordi-quadro', 'progetti', 'miei-progetti'].includes(item.id);
      }
      return true;
    }
    
    // Guest con ambiti multipli: mostra menu per tutti gli ambiti
    if (user.ruolo === 'guest') {
      const menuPerAmbito = {
        'cantierista': ['cantieri'],
        'ingegnere': ['miei-progetti', 'cantieri'],
        'amministrativo': ['clienti', 'preventivi', 'ordini', 'fatturazione'],
        'fattibilita': ['miei-progetti']
      };
      
      // Raccogli tutti i menu permessi per gli ambiti dell'utente
      const menuPermessi = new Set();
      const ambiti = user.ambiti && user.ambiti.length > 0 ? user.ambiti : (user.ambito ? [user.ambito] : []);
      
      ambiti.forEach(amb => {
        const menus = menuPerAmbito[amb] || [];
        menus.forEach(m => menuPermessi.add(m));
      });
      
      if (menuPermessi.size > 0) {
        return menuPermessi.has(item.id);
      }
    }
    
    // Check ruolo
    if (!item.roles.includes(user.ruolo)) return false;
    
    // Check ambito se richiesto (solo per guest)
    if (item.checkAmbito) {
      // Guest deve avere ambito ingegnere o fattibilita
      return userHasAmbito('ingegnere') || userHasAmbito('fattibilita');
    }
    
    return true;
  });

  const handleMenuClick = (path) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <img 
                src="/logo.png" 
                alt="Main Solution Logo" 
                className="h-10 sm:h-16"
              />
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-slate-900">PaMa</h1>
                <p className="text-sm text-slate-600">
                  Benvenuto, {user.nome} {user.cognome}
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium capitalize">
                    {user.ruolo === 'admin' ? 'Admin' : user.ruolo === 'master' ? 'Master' : 'Guest'}
                  </span>
                  {/* Mostra ambiti multipli o singolo */}
                  {(() => {
                    const ambiti = user.ambiti && user.ambiti.length > 0 ? user.ambiti : (user.ambito ? [user.ambito] : []);
                    return ambiti.map(amb => (
                      <span key={amb} className={`ml-1 px-2 py-0.5 text-xs rounded-full font-medium capitalize ${
                        amb === 'amministrativo' ? 'bg-amber-100 text-amber-700' :
                        amb === 'ingegnere' ? 'bg-blue-100 text-blue-700' :
                        amb === 'cantierista' ? 'bg-green-100 text-green-700' :
                        amb === 'fattibilita' ? 'bg-purple-100 text-purple-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {amb === 'fattibilita' ? 'Fattibilità' : amb}
                      </span>
                    ));
                  })()}
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <InstallAppButton />
              {/* Le Mie Attività Button - Sempre visibile per amministrativi, altrimenti solo se ci sono attività prioritarie */}
              {(user.ambito === 'amministrativo' || (mieAttivita && mieAttivita.nuove_attivita > 0)) && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/mie-attivita')}
                  className="border-red-300 hover:bg-red-50 relative"
                >
                  <CheckSquare className="w-4 h-4 mr-2 text-red-600" />
                  <span className="text-red-700 font-medium">Le Mie Attività</span>
                  {mieAttivita && (mieAttivita.nuove_attivita > 0 || mieAttivita.nuove_notifiche > 0) && (
                    <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold shadow-lg">
                      {(mieAttivita.nuove_attivita || 0) + (mieAttivita.nuove_notifiche || 0)}
                    </span>
                  )}
                </Button>
              )}

              <Button
                variant="outline"
                onClick={onLogout}
                data-testid="logout-btn"
                className="border-slate-300"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Menu Cards */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Seleziona un'area</h2>
          <p className="text-slate-600">Scegli la sezione che desideri gestire</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleMenuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.path)}
                data-testid={`menu-${item.id}`}
                className="bg-white rounded-xl shadow-md border border-slate-200 p-6 text-left hover:shadow-lg transition-all duration-200 hover:-translate-y-1 group"
              >
                <div className={`${item.color} ${item.hoverColor} w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-colors`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {item.description}
                </p>
              </button>
            );
          })}
        </div>

        {/* Info box per utenti non-admin */}
        {user.ruolo !== 'admin' && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-1">Accesso Limitato</h4>
                <p className="text-sm text-slate-600">
                  Stai visualizzando solo le sezioni disponibili per il tuo ruolo <strong>{user.ruolo === 'master' ? 'Master' : 'Guest'}</strong>.
                  Contatta un amministratore per richiedere accessi aggiuntivi.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
